import React, { useMemo, useState } from 'react';
import {
  Badge, Card, CardHeader, EmptyState, ProgressBar, Table, Tabs,
} from '@opendate-app/setlist';
import {
  PACING_TABS, dayBadge, filterPacing, paceStatus, pacingFooter,
} from '../../utils/dashboardUtils';
import { currencyFormatter } from '../../utils/reportingUtils';

const PACE_BADGES = {
  ahead: { label: 'Ahead', color: 'success' },
  on_pace: { label: 'On pace', color: 'blue' },
  behind: { label: 'Behind', color: 'warning' },
  sold_out: { label: 'Sold out', color: 'gray' },
};

const muted = { fontSize: 12, color: 'var(--sl-text-tertiary)' };
const formatEventDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');

const COLUMNS = [
  {
    key: 'title',
    header: 'Event',
    primary: true,
    render: (e) => (
      <div>
        <strong>{e.title}</strong>
        <div style={muted}>{e.venue_name}</div>
      </div>
    ),
  },
  {
    key: 'event_date',
    header: 'Date',
    width: 140,
    render: (e) => {
      const badge = e.days_until == null ? null : dayBadge(e.days_until);
      return (
        <div>
          <div style={{ whiteSpace: 'nowrap' }}>{formatEventDate(e.event_date)}</div>
          {badge && <Badge color={badge.color} size="sm">{badge.label}</Badge>}
        </div>
      );
    },
  },
  {
    key: 'sold',
    header: 'Sold / capacity',
    render: (e) => {
      const pct = e.total_capacity > 0 ? Math.round((e.total_tickets_sold / e.total_capacity) * 100) : 0;
      return (
        <div style={{ minWidth: 140 }}>
          <div style={{ fontSize: 13 }}>
            <strong>{e.total_tickets_sold.toLocaleString()}</strong>
            {` / ${e.total_capacity.toLocaleString()}`}
          </div>
          <ProgressBar value={pct} label="right" />
        </div>
      );
    },
  },
  {
    key: 'pace',
    header: 'Pace vs forecast',
    render: (e) => {
      const badge = PACE_BADGES[paceStatus(e)];
      return (
        <div>
          {badge ? <Badge color={badge.color} size="sm">{badge.label}</Badge> : <span style={muted}>No forecast</span>}
          {e.forecast ? <div style={muted}>{`Exp. ${(e.expected_finish || 0).toLocaleString()} · Fcst ${e.forecast.toLocaleString()}`}</div> : null}
        </div>
      );
    },
  },
  {
    key: 'recent',
    header: 'Recent sales',
    width: 150,
    render: (e) => <span style={{ ...muted, whiteSpace: 'nowrap' }}>{`${e.tickets_sold_today} today · ${e.tickets_sold_last_7} this wk`}</span>,
  },
  {
    key: 'net',
    header: 'Net',
    align: 'right',
    render: (e) => currencyFormatter.format(e.net || 0),
  },
];

function ShowPacingCard({ events, loading, error, showTrackerHref, totalUpcoming }) {
  const [tab, setTab] = useState('next_30');
  const rows = useMemo(() => filterPacing(events || [], tab), [events, tab]);
  const footer = loading || error ? null : pacingFooter((events || []).length, totalUpcoming);

  return (
    <Card
      padding="none"
      header={(
        <CardHeader
          title={<a href={showTrackerHref} style={{ color: 'inherit' }}>Event pacing →</a>}
          actions={<Tabs variant="button-border" size="sm" collapseBelow={0} items={PACING_TABS} value={tab} onChange={setTab} />}
        />
      )}
    >
      <Table
        columns={COLUMNS}
        data={rows}
        getRowKey={(e) => e.id}
        loading={loading}
        error={error ? 'Could not load upcoming shows.' : undefined}
        empty={<EmptyState title="No shows match this view" description="Try another tab." />}
      />
      {footer && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--sl-border-secondary)', ...muted }}>
          {`${footer} · `}
          <a href={showTrackerHref}>Open Show Tracker</a>
        </div>
      )}
    </Card>
  );
}

export default ShowPacingCard;
